import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ListaReordenable } from '@/components/ui/ListaReordenable';
import { confirmDialog } from '@/lib/confirmDialog';
import { PRESUPUESTOS_ROUTES, rutaDetallePlantilla } from '../constants/routes';
import { usePlantillas } from '../hooks/usePlantillas';
import { usePresupuestosProformaPermissions } from '../hooks/usePresupuestosProformaPermissions';
import type { Plantilla } from '../types';

/**
 * Autogestión de las plantillas de ítems de presupuesto. Los cambios solo
 * impactan en los presupuestos a los que se aplique la plantilla desde ahora.
 */
export const PlantillasPage: React.FC = () => {
  const navigate = useNavigate();
  const permisos = usePresupuestosProformaPermissions();
  const {
    plantillas,
    isLoading,
    crearPlantilla,
    actualizarPlantilla,
    eliminarPlantilla,
    reordenarPlantillas,
    duplicarPlantilla,
    isGuardando,
  } = usePlantillas();
  const [nombreNueva, setNombreNueva] = useState('');

  const handleCrear = async () => {
    const nombre = nombreNueva.trim();

    if (!nombre) {
      toast.error('El nombre de la plantilla es obligatorio');
      return;
    }

    try {
      const creada = await crearPlantilla({ nombre });
      setNombreNueva('');
      navigate(rutaDetallePlantilla(creada.id));
    } catch {
      toast.error('No se pudo crear la plantilla.');
    }
  };

  const reordenar = async (ids: string[]) => {
    try {
      await reordenarPlantillas(ids);
    } catch {
      toast.error('No se pudieron reordenar las plantillas.');
    }
  };

  const handleEliminar = async (plantilla: Plantilla) => {
    const confirmado = await confirmDialog.open({
      title: `¿Eliminar la plantilla "${plantilla.nombre}"?`,
      text: 'Los presupuestos que ya la usaron conservan sus ítems.',
      confirmButtonText: 'Eliminar',
    });

    if (!confirmado) return;

    try {
      await eliminarPlantilla(plantilla.id);
    } catch {
      toast.error('No se pudo eliminar la plantilla.');
    }
  };

  const handleDuplicar = async (plantilla: Plantilla) => {
    try {
      const copia = await duplicarPlantilla(plantilla.id);
      toast.success(`Se creó "${copia.nombre}".`);
      navigate(rutaDetallePlantilla(copia.id));
    } catch {
      toast.error('No se pudo duplicar la plantilla.');
    }
  };

  const alternarActiva = async (plantilla: Plantilla) => {
    try {
      await actualizarPlantilla({ id: plantilla.id, data: { activo: !plantilla.activo } });
    } catch {
      toast.error('No se pudo actualizar la plantilla.');
    }
  };

  if (!permisos.canManageBases) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        No tenés permisos para administrar las plantillas de presupuesto.
      </div>
    );
  }

  return (
    <div className="space-y-6 p-2 sm:p-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild>
          <Link to={PRESUPUESTOS_ROUTES.LISTADO} aria-label="Volver a presupuestos">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Plantillas de presupuesto</h1>
          <p className="text-sm text-muted-foreground">
            Solo las plantillas activas se ofrecen al crear o importar en un presupuesto. Los
            cambios no modifican los presupuestos ya armados.
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="space-y-4 py-4">
          <div className="flex gap-2">
            <Input
              value={nombreNueva}
              onChange={(event) => setNombreNueva(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') void handleCrear();
              }}
              placeholder="Nombre de la nueva plantilla (ej: Compraventa)"
              aria-label="Nombre de la nueva plantilla"
              maxLength={255}
              disabled={isGuardando}
            />
            <Button onClick={() => void handleCrear()} disabled={isGuardando}>
              <Plus className="mr-1 h-4 w-4" />
              Crear
            </Button>
          </div>

          {isLoading ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Cargando…</p>
          ) : plantillas.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Todavía no hay plantillas
            </p>
          ) : (
            <ListaReordenable
              as="ul"
              items={plantillas}
              onReordenar={(ids) => void reordenar(ids)}
              etiquetaItem={(plantilla) => plantilla.nombre}
              className="space-y-2"
            >
              {(plantilla, asa) => (
                <div
                  data-testid={`plantilla-${plantilla.id}`}
                  className="flex items-center gap-3 rounded-lg border bg-card p-3"
                >
                  {asa}
                  <div className="min-w-0 flex-1">
                    <Link
                      to={rutaDetallePlantilla(plantilla.id)}
                      className="font-semibold underline-offset-4 hover:underline"
                    >
                      {plantilla.nombre}
                    </Link>
                    <p className="text-sm text-muted-foreground">
                      {plantilla.cantidadItems} ítems activos
                    </p>
                  </div>

                  <Button
                    size="sm"
                    variant="ghost"
                    title={plantilla.activo ? 'Desactivar' : 'Activar'}
                    onClick={() => void alternarActiva(plantilla)}
                    disabled={isGuardando}
                  >
                    <Badge variant={plantilla.activo ? 'default' : 'secondary'}>
                      {plantilla.activo ? 'Activa' : 'Inactiva'}
                    </Badge>
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    title="Duplicar plantilla"
                    aria-label={`Duplicar plantilla ${plantilla.nombre}`}
                    onClick={() => void handleDuplicar(plantilla)}
                    disabled={isGuardando}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    title="Eliminar plantilla"
                    aria-label={`Eliminar plantilla ${plantilla.nombre}`}
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => void handleEliminar(plantilla)}
                    disabled={isGuardando}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </ListaReordenable>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
